// Typed wrappers for /parent/account/* — the parent self-service settings
// surface. Track C.
//
// Mirrors ~/lib/billing.ts: thin functions over `api()`, no state, so the
// screens stay declarative and the error envelope surfaces as ApiError.

import { api, API_BASE } from './api';

export type Locale = 'ar' | 'en';

/** The literal the user must type to confirm erasure. Fixed by the API. */
export const DELETE_CONFIRMATION_WORD = 'DELETE';

export interface AccountExport {
  exportedAt: string;
  account: {
    id: string;
    email: string | null;
    username: string | null;
    locale: string;
    emailVerifiedAt: string | null;
    createdAt: string;
    displayName: string | null;
    country: string | null;
    phone: string | null;
    timezone: string | null;
    marketingOptIn: string | null;
  };
  children: Array<{
    id: string;
    username: string | null;
    displayName: string | null;
    grade: string | null;
    createdAt: string;
  }>;
  subscriptions: Array<{
    id: string;
    status: string;
    planSlug: string | null;
    currentPeriodEnd: string | null;
    createdAt: string;
  }>;
  invoices: Array<{
    id: string;
    status: string;
    currency: string;
    amountMinor: number;
    issuedAt: string;
  }>;
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await api('/parent/account/password', {
    method: 'PATCH',
    body: { currentPassword, newPassword },
  });
}

export async function requestEmailChange(newEmail: string, currentPassword: string): Promise<void> {
  await api('/parent/account/email/request', {
    method: 'POST',
    body: { newEmail, currentPassword },
  });
}

export async function confirmEmailChange(token: string): Promise<void> {
  await api('/parent/account/email/confirm', { method: 'POST', body: { token } });
}

export async function changeLocale(locale: Locale): Promise<void> {
  await api('/parent/account/locale', { method: 'PATCH', body: { locale } });
}

export async function exportAccountData(signal?: AbortSignal): Promise<AccountExport> {
  return api<AccountExport>('/parent/account/export', { signal });
}

export async function deleteAccount(currentPassword?: string): Promise<void> {
  await api('/parent/account', {
    method: 'DELETE',
    body: {
      confirmation: DELETE_CONFIRMATION_WORD,
      ...(currentPassword ? { currentPassword } : {}),
    },
  });
}

/**
 * Hand the export to the browser as a downloaded file.
 *
 * Built from an in-memory Blob rather than pointing an <a> at the API URL:
 * the endpoint needs the session cookie AND is rate-limited, and a bare link
 * would also open the JSON inline in a new tab on some browsers.
 */
export function downloadJson(data: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick — revoking synchronously can race the download
  // in Safari.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Exposed for tests/debugging only — the app never builds URLs by hand. */
export const ACCOUNT_API_BASE = API_BASE;
